(function (root) {
  'use strict';
  const ns = root.FKKInsurance = root.FKKInsurance || {};
  // Versioned key records only explicit choices made with this controller.
  const preferenceKey = 'fkk_insurance_choice_v1';

  function normalizeConfig(raw) {
    const productId = String(raw.productId || '');
    if (!/^\d+$/.test(productId)) throw new Error('insurance_configuration');
    if (!['fixed', 'percent'].includes(raw.pricingType)) throw new Error('insurance_configuration');
    return {
      productId,
      pricingType: raw.pricingType,
      variants: raw.pricingType === 'fixed' ? raw.fixedVariants || [] : raw.percentVariants || [],
      percent: Number(raw.widgetInfo?.shippingPercent || 0),
      defaultEnabled: raw.widgetInfo?.frontDeskWidgetEnabled === true,
      title: raw.widgetInfo?.widgetName || 'Delivery Insurance',
      description: raw.widgetInfo?.widgetDescription || '',
    };
  }

  function insuranceItems(cart, config) {
    return (cart.items || []).filter((item) => String(item.product_id) === config.productId);
  }

  function merchandiseCount(cart, config) {
    return (cart.items || []).reduce((count, item) => (
      String(item.product_id) === config.productId ? count : count + Number(item.quantity || 0)
    ), 0);
  }

  function baseTotal(cart, config) {
    const insuranceTotal = insuranceItems(cart, config).reduce((sum, item) => (
      sum + Number(item.final_line_price ?? item.line_price ?? 0)
    ), 0);
    return Math.max(0, Number(cart.total_price) - insuranceTotal);
  }

  function quote(cart, config) {
    const currency = String(cart.currency || '').toUpperCase();
    const variants = config.variants.map((variant) => {
      const rawPrice = variant.prices?.[currency];
      const price = rawPrice === undefined || rawPrice === null || rawPrice === '' ? NaN : Number(rawPrice);
      return { id: String(variant.id || ''), cents: Math.round(price * 100) };
    }).filter((variant) => /^\d+$/.test(variant.id) && Number.isFinite(variant.cents) && variant.cents >= 0);
    if (!variants.length || !currency) return null;
    if (config.pricingType === 'fixed') return variants[0];
    if (!Number.isFinite(config.percent) || config.percent < 0) return null;
    variants.sort((a, b) => a.cents - b.cents);
    const target = Math.round(baseTotal(cart, config) * config.percent / 100);
    return variants.find((variant) => variant.cents >= target) || variants[variants.length - 1];
  }

  class InsuranceState {
    constructor(storage) {
      this.storage = storage;
      this.config = null;
      this.cart = null;
      this.enabled = false;
      this.preferenceVersion = 0;
      this.readVersion = 0;
      this.busy = true;
      this.error = null;
    }

    configure(config) {
      this.config = config;
      let saved;
      try { saved = this.storage?.getItem(preferenceKey); } catch (_) { saved = null; }
      // Old supplier values may have been auto-written; do not migrate them as consent.
      // A fresh session starts off, regardless of the supplier's default configuration.
      this.enabled = saved === 'true';
    }

    choose(enabled) {
      this.enabled = Boolean(enabled);
      this.preferenceVersion += 1;
      try { this.storage?.setItem(preferenceKey, String(this.enabled)); } catch (_) { /* Keep this page's choice. */ }
    }

    accept(cart, version) {
      if (version < this.readVersion) return false;
      this.readVersion = version;
      this.cart = cart;
      return true;
    }

    snapshot() {
      const configured = Boolean(this.config && this.cart);
      const count = configured ? merchandiseCount(this.cart, this.config) : 0;
      const price = configured ? quote(this.cart, this.config) : null;
      const subtotal = configured ? baseTotal(this.cart, this.config) : null;
      return {
        config: this.config,
        ready: configured,
        count,
        enabled: this.enabled,
        fee: price?.cents ?? null,
        total: subtotal === null ? null : subtotal + (this.enabled && count && price ? price.cents : 0),
        currency: this.cart?.currency || '',
        busy: this.busy,
        error: this.error,
      };
    }
  }

  Object.assign(ns, { InsuranceState, normalizeConfig, insuranceItems, merchandiseCount, baseTotal, quote });
  if (typeof module !== 'undefined') module.exports = ns;
})(globalThis);
